import axios from "axios"
import createAuthRefreshInterceptor from "axios-auth-refresh"
import { notification } from "antd"
import { removeAuthLocal } from "../lib/localstorage"

// Dùng đường dẫn tương đối để chắc chắn luôn có prefix /api/v1 (đã cấu hình ở backend)
const REFRESH_URL = "/api/v1/internal/token/refresh/"

function extractApiMessage(data) {
    if (data == null) return null
  if (typeof data === "string") return data.trim() || null
  if (typeof data !== "object") return null
  if (typeof data.message === "string" && data.message.trim()) return data.message
  if (typeof data.error_message === "string" && data.error_message.trim()) return data.error_message
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail
  if (Array.isArray(data.detail) && data.detail.length) {
    return data.detail
      .map((x) => (typeof x === "string" ? x : x?.msg || x?.message || ""))
      .filter(Boolean)
      .join("; ")
  }
  // EES: { error: { code, message } }
  if (data.error && typeof data.error === "object") {
    if (typeof data.error.message === "string" && data.error.message.trim()) return data.error.message
    if (Array.isArray(data.error.details) && data.error.details.length) {
      return data.error.details
        .map((x) => (typeof x === "string" ? x : x?.message || ""))
        .filter(Boolean)
        .join("; ")
    }
  }
  if (typeof data.error === "string" && data.error.trim()) return data.error
  return null
}

/** Lỗi theo từng field kiểu { qty: ["..."] } — không phải envelope { error: {...} }. */
function isFieldLevelError(data) {
  if (!data || typeof data !== "object") return false
  if (data.error || data.detail || data.message || data.error_message) return false
  return Object.values(data).some((v) => Array.isArray(v))
}

/**
 * Refresh auth using HTTP-only cookie.
 * Server reads refresh_token from cookie, returns new access_token in cookie.
 */
const refreshAuthLogic = (failedRequest) =>
  axios
    .post(REFRESH_URL, {}, { withCredentials: true })  // Send cookies
    .then(() => {
      // Server sets new tokens in HTTP-only cookies automatically
      // No need to update localStorage
      return Promise.resolve()
    })
    .catch((error) => {
      // Token expired or invalid — clear cookies and redirect to login
      const onLoginPage = typeof window !== "undefined" && window.location.pathname === "/login"
      if (onLoginPage) {
        return Promise.reject()
      }
      
      removeAuthLocal()
      
      // Reload page to trigger auth check (server will redirect to login)
      if (typeof window !== "undefined") {
        window.location.href = "/login"
      }
      return Promise.reject()
    })

export default function getInstanceAxios(baseAPI) {
  const instance = axios.create({
    baseURL: baseAPI
  })

  instance.interceptors.request.use(
    function (config) {
      // Always send cookies for cross-origin requests (CSRF protection)
      config.withCredentials = true
      
      // Authorization header is NOT needed when using HTTP-only cookies.
      // Server reads access_token from cookie directly.
      config.headers = {
        ...(config.headers || {}),
        Accept: "application/json",
        "Content-Type": "application/json",
      }
      return config
    },
    function (error) {
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    function (response) {
      if (response.status >= 200 && response.status < 300) return response.data
      return Promise.reject(response.data)
    },
    function (error) {
      if (error.response) {
        const { response } = error
        const data = response?.data
        const method = (response.config?.method || "").toLowerCase()
        const status = response.status
        if (data && status !== 500) {
          const msg = extractApiMessage(data) || "Có lỗi xảy ra"
          const fieldLevel = isFieldLevelError(data)
          const showForGet = method === "get" && [400, 403, 404, 409].includes(status)
          // 409 luôn hiện thông báo (vd. trùng nhật ký ngày)
          const showAlways = status === 409 || status === 403
          if (showAlways || (method !== "get" && !fieldLevel) || showForGet) {
            notification.error({
              message: status === 409 ? "Không thể tạo" : "Có lỗi xảy ra",
              description: msg,
              duration: 5
            })
          }
        }
      }
      return Promise.reject(error)
    }
  )

  createAuthRefreshInterceptor(instance, refreshAuthLogic)
  return instance
}
